/**
 * executive-report-brief.ts — Editorial copy layer for the Executive Report
 * standfirst.
 *
 * Mirrors `posture-brief.ts` and the per-pillar composers in shape, seeding,
 * and voice rules. The Executive Report tells a longer-arc story than the
 * Command Center daily brief: we frame the verdict against a configurable
 * reporting period (30 / 60 / 90 days), not "today", and the narrative wraps a
 * board-ready summary rather than the operational queue.
 *
 * Voice rules (also enforced by tests):
 *   - Verdicts cap at six words.
 *   - Headlines avoid em-dashes (period-then-newline reads cleaner).
 *   - Kicker is "Executive Brief · {N} days" — not "Today's Brief". The
 *     report is a period summary, not a daily snapshot.
 *   - Loading state when `score == null` returns a neutral "Compiling…" brief
 *     so a page that mounts before the report query resolves doesn't flash an
 *     authoritative-but-wrong worst-band verdict.
 *
 * Verdict-picker rules (encoded in `pickVerdict`):
 *   1. score >= 90                          → VERDICT_EXCELLENT
 *   2. score >= 75, no laggard              → VERDICT_STRONG_NO_LAGGARD
 *   3. score >= 75, weakest pillar < 70     → VERDICT_STRONG_LAGGARD ({pillar})
 *   4. score >= 60                          → VERDICT_NEEDS_ATTENTION ({verb})
 *   5. score < 60                           → VERDICT_CRITICAL ({pillar} or no-laggard)
 *   6. score == null                        → loading brief
 *
 * The "weakest pillar" treats the four ExecutiveReportData score components
 * (Secure Score, BPA, MFA Adoption, Baseline Compliance) as candidates and
 * picks the lowest defined one, matching the score-box panel labels.
 */

import { cap, formatKicker, localDayKey, pickVariant } from './brief-helpers'
import type { ExecutiveReportData } from '@/types/executiveReport'

/* -------------------------------------------------------------------------- */
/* Public shape                                                               */
/* -------------------------------------------------------------------------- */

export interface ExecutiveReportBrief {
  /** Pill above the headline: "Executive Brief · 30 days" */
  kicker: string
  /** Roman portion of the headline, e.g. "Posture is" */
  headlineSubject: string
  /** Italic emphasis word, e.g. "strong" — gets `<em>` treatment */
  headlineModifier: string
  /** Second sentence on the headline line. Capitalized, period-terminated. */
  weakLink: string
  /** Single-paragraph deck under the headline. Uses `**bold**` markers. */
  deck: string
  /** Six-words-or-fewer verdict in the score box, italic, in quotes */
  verdict: string
  /** Byline footer with `**bold**` markers around author + tenant. */
  byline: string
  /** Score delta vs N days ago, drives the green pill */
  delta: { value: number; days: number }
  /** Italic deck for the AI narrative section, derived deterministically. */
  narrativeDeck: string
}

/** Compact pillar key — used for the verdict {pillar} substitution and the
 *  weakest-component lookup. */
export type ExecutiveComponentKey = 'secure' | 'bpa' | 'mfa' | 'baseline'

/**
 * Single-word component names — used inside the six-word verdict cap so
 * "Best Practice" / "Secure Score" don't blow past the budget. This mirrors
 * `posture-brief.ts`'s PILLAR_LABEL_SHORT split (where `bpa` collapses to
 * "Practice"). The full editorial labels live in COMPONENT_LABEL_FULL and
 * are used in the deck/weak-link sentences where there's room.
 */
const COMPONENT_LABEL_SHORT: Record<ExecutiveComponentKey, string> = {
  secure: 'Secure',
  bpa: 'Practice',
  mfa: 'MFA',
  baseline: 'Baseline',
}

const COMPONENT_LABEL_FULL: Record<ExecutiveComponentKey, string> = {
  secure: 'Microsoft Secure Score',
  bpa: 'Best Practice analyzer',
  mfa: 'MFA adoption',
  baseline: 'Baseline compliance',
}

export interface ExecutiveReportBriefInput {
  /** Active tenant ID — drives the seed for variant selection. */
  tenantId?: string
  /** Friendly tenant name for the byline — falls back to "your tenant". */
  tenantName?: string
  /** Composite Guardian Score (0-100). When null we return the loading brief. */
  score?: number | null
  /** Period-start composite Guardian Score, used for the 30/60/90d delta. */
  periodStartScore?: number | null
  /** Reporting window in days. Drives the kicker and the byline period chip. */
  periodDays?: number
  /** Per-component scores, in 0-100 percentage form. Each may be null when
   *  the underlying signal hasn't arrived yet. */
  components?: {
    secureScore?: number | null
    bpa?: number | null
    mfa?: number | null
    baseline?: number | null
  }
  /** Total open critical violations — drives the weak-link clause. */
  openCriticalViolations?: number
  /** Total open high violations — used for the weak-link compound branch. */
  openHighViolations?: number
  /** Whether the AI narrative is present. Drives `narrativeDeck` framing. */
  hasNarrative?: boolean
  /** Override "today" — primarily for testing the kicker. */
  asOfDate?: Date
}

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Returns the lowest-scoring component, but ONLY when at least one component
 * is defined. Same loading-race guard the Command Center brief uses: refuse
 * to name a "weakest" pillar from partial data — it leads the user toward the
 * wrong fix.
 *
 * Unlike `posture-brief.ts` (which requires ALL pillars to be defined), the
 * Executive Report report DTO carries all four components atomically (they
 * come back in the same payload), so the loading-race surface is smaller.
 * We still bail when none are defined to avoid a default-pick.
 */
function weakestComponent(
  c: ExecutiveReportBriefInput['components'],
): { key: ExecutiveComponentKey; score: number } | undefined {
  if (!c) return undefined
  const candidates: { key: ExecutiveComponentKey; score: number | null | undefined }[] = [
    { key: 'secure', score: c.secureScore },
    { key: 'bpa', score: c.bpa },
    { key: 'mfa', score: c.mfa },
    { key: 'baseline', score: c.baseline },
  ]
  let best: { key: ExecutiveComponentKey; score: number } | undefined
  for (const cand of candidates) {
    if (typeof cand.score !== 'number') continue
    if (!best || cand.score < best.score) {
      best = { key: cand.key, score: cand.score }
    }
  }
  return best
}

/** Format the kicker for the executive period — distinct from the daily
 *  "Today's Brief" framing the Command Center uses. The report is a period
 *  summary, so the kicker reflects that. */
function formatPeriodKicker(periodDays: number): string {
  return `Executive Brief · ${periodDays} days`
}

/** Drop trailing punctuation that may slip through a freeform input. */
function trimTrailingPunct(s: string): string {
  return s.replace(/[.,;:!?]+$/, '')
}

/* -------------------------------------------------------------------------- */
/* Verdict variants (six-words-or-fewer per the ScoreBox cap)                 */
/* -------------------------------------------------------------------------- */

const VERDICT_EXCELLENT: readonly string[] = [
  'Excellent. Steady at the top.',
  'Top form across the board.',
  'Excellent posture. Keep cadence.',
  'Strong across every pillar.',
]

const VERDICT_STRONG_NO_LAGGARD: readonly string[] = [
  'Strong overall. One thing to watch.',
  'Solid period. Keep momentum.',
  'Healthy posture. Routine watch.',
]

/** Templates take a `{pillar}` token (short component label). */
const VERDICT_STRONG_LAGGARD: readonly string[] = [
  'Strong overall. {pillar} needs work.',
  'Good shape. Lift {pillar} next.',
  'Healthy. {pillar} is the soft spot.',
]

/** Bands 60-74. Templates take a `{verb}` token (capitalized imperative). */
const VERDICT_NEEDS_ATTENTION: readonly string[] = [
  'Mixed. {verb} first.',
  'Mid-stride. {verb} this period.',
  'Steady but soft. {verb} now.',
]

/** Templates take a `{pillar}` token (short component label). */
const VERDICT_CRITICAL: readonly string[] = [
  'Plenty to clean up. {pillar} first.',
  'Soft floor. Begin with {pillar}.',
  'Several gaps. {pillar} first.',
]

/** No-laggard fallback for the critical band — used when we don't have
 *  enough signal to honestly name a starting pillar. */
const VERDICT_CRITICAL_NO_LAGGARD: readonly string[] = [
  'Plenty to triage. Start broad.',
  'Soft floor. Plenty to clean up.',
  'Several gaps. Start broad.',
]

/** Returned when score is null/undefined — the page shouldn't pick a band. */
const VERDICT_LOADING = 'Compiling the brief…'

/** Verb library for the imperative-borrowing branch. Same approach the per-
 *  pillar composers use; the default ("Triage") is editorially neutral. */
const KNOWN_VERBS = new Set([
  'enforce',
  'roll',
  'reclaim',
  'review',
  'remove',
  'rotate',
  'restrict',
  'require',
  'rebuild',
  'remediate',
  'audit',
  'patch',
  'disable',
  'block',
  'tighten',
  'triage',
  'address',
  'harden',
  'close',
  'lock',
])

function imperativeFromComponent(weakest: ExecutiveComponentKey | undefined): string {
  // Map the weakest component to a verb that reads naturally in board copy.
  // The per-pillar composers default to "triage"; we lean into the named
  // pillar when we have one so the verdict steers the executive somewhere.
  switch (weakest) {
    case 'secure':
      return 'tighten'
    case 'bpa':
      return 'harden'
    case 'mfa':
      return 'enforce'
    case 'baseline':
      return 'remediate'
    default:
      return 'triage'
  }
}

function pickVerdict(
  score: number,
  weakest: { key: ExecutiveComponentKey; score: number } | undefined,
  seed: string,
): string {
  if (score >= 90) {
    return pickVariant(VERDICT_EXCELLENT, seed, 'verdict.excellent')
  }
  if (score >= 75) {
    if (!weakest || weakest.score >= 70) {
      return pickVariant(VERDICT_STRONG_NO_LAGGARD, seed, 'verdict.strong')
    }
    const tpl = pickVariant(VERDICT_STRONG_LAGGARD, seed, 'verdict.laggard')
    return tpl.replace('{pillar}', COMPONENT_LABEL_SHORT[weakest.key])
  }
  if (score >= 60) {
    const verbBase = imperativeFromComponent(weakest?.key)
    const verb = cap(KNOWN_VERBS.has(verbBase) ? verbBase : 'triage')
    const tpl = pickVariant(VERDICT_NEEDS_ATTENTION, seed, 'verdict.needsAttention')
    return tpl.replace('{verb}', verb)
  }
  // score < 60 — critical band
  if (!weakest) {
    return pickVariant(VERDICT_CRITICAL_NO_LAGGARD, seed, 'verdict.critical.noLaggard')
  }
  const tpl = pickVariant(VERDICT_CRITICAL, seed, 'verdict.critical')
  return tpl.replace('{pillar}', COMPONENT_LABEL_SHORT[weakest.key])
}

/* -------------------------------------------------------------------------- */
/* Headline modifier (italic emphasis word — single-word table, no variants)  */
/* -------------------------------------------------------------------------- */

/**
 *   ≥85 with positive delta → "lifting"
 *   ≥85 with flat/negative   → "holding"
 *   70-84 with positive delta → "strengthening"
 *   70-84 with flat/negative  → "mixed"
 *   55-69                     → "weakening"
 *   <55                       → "fragile"
 */
function pickModifier(score: number, deltaValue: number): string {
  if (score >= 85) return deltaValue > 0 ? 'lifting' : 'holding'
  if (score >= 70) return deltaValue > 0 ? 'strengthening' : 'mixed'
  if (score >= 55) return 'weakening'
  return 'fragile'
}

/* -------------------------------------------------------------------------- */
/* Weak-link sentence                                                         */
/* -------------------------------------------------------------------------- */

const WEAKLINK_ZERO: readonly string[] = [
  'No critical items on the board.',
  'The critical lane is clear.',
  'Nothing critical outstanding.',
]

const WEAKLINK_ONE: readonly string[] = [
  'One critical item still open.',
  'A single critical item stands out.',
  'One thing is dragging the score down.',
]

/** Templates take an `{n}` token (word form: "Two" / "Three"). */
const WEAKLINK_FEW: readonly string[] = [
  '{n} critical items still open.',
  '{n} things to close this period.',
  '{n} items are dragging the score down.',
]

/** Templates take a `{pillar}` token (full component label). */
const WEAKLINK_LAGGARD: readonly string[] = [
  '{pillar} is the soft spot.',
  '{pillar} is the holdout.',
  '{pillar} is dragging the score down.',
]

const WEAKLINK_BROAD: readonly string[] = [
  'Multiple criticals in play.',
  'Critical items are stacking up.',
  'Posture is moving the wrong way.',
]

const WEAKLINK_TIGHTEN: readonly string[] = [
  'Room to tighten.',
  'Routine cleanup remains.',
  'Hygiene work to round it out.',
]

function buildWeakLink(
  criticalCount: number | undefined,
  weakest: { key: ExecutiveComponentKey; score: number } | undefined,
  seed: string,
): string {
  const numberWord = (n: number): string =>
    ['zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six'][n] ?? String(n)

  if (typeof criticalCount === 'number' && criticalCount > 0) {
    if (criticalCount === 1) return pickVariant(WEAKLINK_ONE, seed, 'weakLink.one')
    if (criticalCount <= 3) {
      const tpl = pickVariant(WEAKLINK_FEW, seed, 'weakLink.few')
      return tpl.replace('{n}', numberWord(criticalCount))
    }
    if (weakest) {
      const tpl = pickVariant(WEAKLINK_LAGGARD, seed, 'weakLink.laggard')
      return tpl.replace('{pillar}', COMPONENT_LABEL_FULL[weakest.key])
    }
    return pickVariant(WEAKLINK_BROAD, seed, 'weakLink.broad')
  }

  if (typeof criticalCount === 'number' && criticalCount === 0) {
    if (weakest && weakest.score < 70) {
      const tpl = pickVariant(WEAKLINK_LAGGARD, seed, 'weakLink.laggardZeroCrit')
      return tpl.replace('{pillar}', COMPONENT_LABEL_FULL[weakest.key])
    }
    return pickVariant(WEAKLINK_ZERO, seed, 'weakLink.zero')
  }

  return pickVariant(WEAKLINK_TIGHTEN, seed, 'weakLink.tighten')
}

/* -------------------------------------------------------------------------- */
/* Deck (trend + risk + recommended action)                                   */
/* -------------------------------------------------------------------------- */

const TREND_LIFTING_FAST: readonly string[] = [
  'Posture climbed steadily across the period, lifted by gains in every component.',
  'Real momentum across the period. The score moved up meaningfully.',
  'The score climbed across the period, with gains spread broadly.',
]

const TREND_LIFTING: readonly string[] = [
  'Posture ticked up across the period, with steady progress on the components.',
  'Small gains across the period, spread across the four components.',
  'The score is up modestly across the period.',
]

const TREND_FLAT: readonly string[] = [
  'Posture held flat across the period. Neither gaining nor giving up ground.',
  "The score didn't move across the period.",
  'Steady-state posture for the period, with no meaningful shift in the underlying signals.',
]

const TREND_SLIPPING: readonly string[] = [
  'Posture slipped slightly across the period. Nothing alarming, but worth a look.',
  'The score is down a touch across the period. Worth a look when you can.',
  'Modest decline across the period. Not yet a trend, but worth watching.',
]

const TREND_FALLING: readonly string[] = [
  'Posture fell meaningfully across the period. Needs eyes on it next sprint.',
  'The score dropped sharply across the period.',
  'Significant decline across the period. This needs a real response, not next quarter.',
]

/** Templates take a `{pillar}` token (already-bolded full component label). */
const RISK_PILLAR: readonly string[] = [
  '{pillar} is the soft spot this period. Lifting it is where your time pays back fastest.',
  '{pillar} is the one slowing you down. Lifting it moves the score most.',
  '{pillar} is dragging the score down right now.',
]

const RISK_HYGIENE: readonly string[] = [
  'No single risk stands out. The remaining gaps are routine hygiene rather than headline failures.',
  'The remaining items are hygiene rather than headline risks.',
  "Nothing dominates the risk picture. It's housekeeping from here.",
]

const RISK_COMPOUND: readonly string[] = [
  'Several smaller gaps add up. Closing the biggest lifts the score fastest.',
  'A handful of smaller items stack up. The biggest of them is the place to start.',
  'Several smaller things stack up. Tackling the biggest lifts the score fastest.',
]

const ACTION_HOLD: readonly string[] = [
  "Keep doing what you're doing. The usual review cadence is enough.",
  'Keep it steady. The usual review cadence is enough.',
  'Maintain cadence on the existing review schedule.',
]

const ACTION_LEAD: readonly string[] = [
  'Take the lead item this sprint to keep momentum.',
  'Take the lead item next sprint.',
  'Knock out the lead item this sprint to maintain momentum.',
]

const ACTION_TRIAGE: readonly string[] = [
  'Take the highest-impact items first. The rest can wait.',
  'Focus on the highest-impact items first; the rest holds.',
  'Take the high-impact items first and let the rest wait.',
]

const ACTION_PUSH: readonly string[] = [
  'This needs a real sprint of focus, not the usual cadence.',
  'Plan a focused remediation push next sprint.',
  'This is a remediation sprint, not business-as-usual.',
]

function buildDeck(
  score: number,
  deltaValue: number,
  weakest: { key: ExecutiveComponentKey; score: number } | undefined,
  seed: string,
): string {
  const trend = (() => {
    if (deltaValue >= 3) return pickVariant(TREND_LIFTING_FAST, seed, 'deck.trend.liftingFast')
    if (deltaValue > 0) return pickVariant(TREND_LIFTING, seed, 'deck.trend.lifting')
    if (deltaValue === 0) return pickVariant(TREND_FLAT, seed, 'deck.trend.flat')
    if (deltaValue >= -3) return pickVariant(TREND_SLIPPING, seed, 'deck.trend.slipping')
    return pickVariant(TREND_FALLING, seed, 'deck.trend.falling')
  })()

  const risk = (() => {
    if (weakest && weakest.score < 70) {
      const pillar = `**${COMPONENT_LABEL_FULL[weakest.key]}**`
      const tpl = pickVariant(RISK_PILLAR, seed, 'deck.risk.pillar')
      return tpl.replace('{pillar}', pillar)
    }
    if (score >= 85) return pickVariant(RISK_HYGIENE, seed, 'deck.risk.hygiene')
    return pickVariant(RISK_COMPOUND, seed, 'deck.risk.compound')
  })()

  const action = (() => {
    if (score >= 90) return pickVariant(ACTION_HOLD, seed, 'deck.action.hold')
    if (score >= 75) return pickVariant(ACTION_LEAD, seed, 'deck.action.lead')
    if (score >= 60) return pickVariant(ACTION_TRIAGE, seed, 'deck.action.triage')
    return pickVariant(ACTION_PUSH, seed, 'deck.action.push')
  })()

  return `${trend} ${risk} ${action}`
}

/* -------------------------------------------------------------------------- */
/* Narrative deck (frames the AI executive narrative section)                 */
/* -------------------------------------------------------------------------- */

const NARRATIVE_DECK_PRESENT: readonly string[] = [
  'Guardian read the period in full. The board summary follows.',
  'A board-ready read of the period. Guardian assembled the narrative below.',
  'Guardian distilled the period into a board-ready summary.',
]

const NARRATIVE_DECK_ABSENT: readonly string[] = [
  'No board-ready summary for this period. The numbers tell the story.',
  'No narrative compiled for this period yet. The data still speaks for itself.',
]

function buildNarrativeDeck(hasNarrative: boolean, seed: string): string {
  if (hasNarrative) {
    return pickVariant(NARRATIVE_DECK_PRESENT, seed, 'narrative.deck.present')
  }
  return pickVariant(NARRATIVE_DECK_ABSENT, seed, 'narrative.deck.absent')
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose an ExecutiveReportBrief from the active report state. All inputs
 * are optional — the function degrades gracefully toward a plausible "no data"
 * brief rather than throwing.
 *
 * When `input.score` is null/undefined we return a neutral "Compiling…" brief
 * rather than coercing to 0 (which would land the loading state in the
 * worst-band "fragile / critical" verdict for a fraction of a second).
 *
 * `score === 0` is a legitimate value (brand-new tenant, broken connectors)
 * and still routes through the normal band logic.
 */
export function composeExecutiveReportBrief(
  input: ExecutiveReportBriefInput = {},
): ExecutiveReportBrief {
  const periodDays = typeof input.periodDays === 'number' ? input.periodDays : 30
  const tenantName = input.tenantName?.trim() ? input.tenantName : 'your tenant'
  const now = input.asOfDate ?? new Date()

  // Compute the delta from period-start when both points are present.
  const deltaValue =
    typeof input.score === 'number' && typeof input.periodStartScore === 'number'
      ? Math.round((input.score - input.periodStartScore) * 10) / 10
      : 0

  const byline = `By **Guardian** · ${formatKicker(now).replace(/^Today's Brief · /, '')} · **${tenantName}** · ${periodDays}-day period`

  if (input.score == null) {
    return {
      kicker: formatPeriodKicker(periodDays),
      headlineSubject: 'Posture is',
      headlineModifier: 'compiling',
      weakLink: 'Reading the latest signal.',
      deck: 'Pulling the executive view together. The brief updates once the report assembles.',
      verdict: VERDICT_LOADING,
      byline,
      delta: { value: deltaValue, days: periodDays },
      narrativeDeck: 'Guardian is drafting the board summary.',
    }
  }

  const score = input.score
  const weakest = weakestComponent(input.components)

  // Daily seed: stable per (tenant, local day, period). The period is folded
  // into the seed so flipping 30 → 90 days re-rolls the variant — different
  // window, different framing reads cleaner than the same line repeated.
  const seed = `${input.tenantId ?? 'guardian'}:${localDayKey(now)}:${periodDays}`

  const headlineModifier = pickModifier(score, deltaValue)
  const weakLink = buildWeakLink(input.openCriticalViolations, weakest, seed)
  const verdict = pickVerdict(score, weakest, seed)
  const deck = buildDeck(score, deltaValue, weakest, seed)
  const narrativeDeck = buildNarrativeDeck(input.hasNarrative === true, seed)

  return {
    kicker: formatPeriodKicker(periodDays),
    headlineSubject: 'Posture is',
    headlineModifier,
    weakLink,
    deck,
    verdict,
    byline,
    delta: { value: deltaValue, days: periodDays },
    narrativeDeck,
  }
}

/* -------------------------------------------------------------------------- */
/* Mapper from raw DTO → composer input                                       */
/* -------------------------------------------------------------------------- */

/**
 * Convenience adapter: collapses an `ExecutiveReportData` DTO into the brief
 * composer input. Keeps the page component free of coercion logic.
 */
export function executiveReportToBriefInput(
  report: ExecutiveReportData | null | undefined,
  opts: { tenantId?: string; tenantName?: string; periodDays?: number } = {},
): ExecutiveReportBriefInput {
  if (!report) {
    return {
      tenantId: opts.tenantId,
      tenantName: opts.tenantName,
      periodDays: opts.periodDays,
      score: null,
    }
  }
  return {
    tenantId: opts.tenantId,
    tenantName: opts.tenantName ?? report.tenantName,
    periodDays: opts.periodDays ?? report.reportPeriodDays,
    score: report.currentGuardianScore,
    periodStartScore: report.periodStartGuardianScore,
    components: {
      secureScore: report.secureScore?.current ?? null,
      bpa: report.bpaScore?.current ?? null,
      mfa: report.mfaAdoption?.current ?? null,
      baseline: report.baselineCompliance?.current ?? null,
    },
    openCriticalViolations: report.metrics?.openCriticalViolations,
    openHighViolations: report.metrics?.openHighViolations,
    hasNarrative: typeof report.executiveNarrative === 'string' && report.executiveNarrative.trim().length > 0,
  }
}

// Trim helper exposed for tests that exercise the narrative path. Kept inside
// the module so the public API stays narrow.
void trimTrailingPunct

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Variant tables exposed for shape-assertions in tests. Not part of the
 *  public runtime API; do not import from app code. */
export const __TEST_ONLY__ = {
  VERDICT_EXCELLENT,
  VERDICT_STRONG_NO_LAGGARD,
  VERDICT_STRONG_LAGGARD,
  VERDICT_NEEDS_ATTENTION,
  VERDICT_CRITICAL,
  VERDICT_CRITICAL_NO_LAGGARD,
  VERDICT_LOADING,
  WEAKLINK_ZERO,
  WEAKLINK_ONE,
  WEAKLINK_FEW,
  WEAKLINK_LAGGARD,
  WEAKLINK_BROAD,
  WEAKLINK_TIGHTEN,
  NARRATIVE_DECK_PRESENT,
  NARRATIVE_DECK_ABSENT,
  COMPONENT_LABEL_SHORT,
  COMPONENT_LABEL_FULL,
}
